import type { IFileItem } from '../models/FileItem';
import type { IIssue } from '../models/Issue';
import type { IScanResult } from '../models/ScanResult';
import { fileBaseName, filePath, groupBy, stripCopyCounter, values } from './ruleUtils';
import { getSystemFileSource } from './FilenameRule';

/** System files (e.g. .DS_Store in every folder) are expected to repeat; they have their own rule. */
function candidateFiles(scan: IScanResult): IFileItem[] {
  return values(scan.files).filter(f => f.size > 0 && !getSystemFileSource(f));
}

/**
 * Exact duplicates: identical quickXorHash and size. Empty files are excluded — they all
 * share a hash and are reported by the empty-file rule instead.
 */
export function findExactDuplicates(scan: IScanResult): IIssue[] {
  const files: IFileItem[] = candidateFiles(scan).filter(f => !!f.quickXorHash);
  const groups: IFileItem[][] = values(groupBy(files, f => `${f.quickXorHash}|${f.size}`))
    .filter(g => g.length > 1);

  return groups.map(group => {
    const sorted: IFileItem[] = group.slice().sort((a, b) => filePath(a).localeCompare(filePath(b)));
    return {
      id: `duplicates:${sorted[0].id}`,
      category: 'duplicates',
      severity: 'medium',
      message: `${sorted.length} identical copies of "${sorted[0].name}"`,
      fileIds: sorted.map(f => f.id),
      folderIds: []
    };
  });
}

/** Name with copy markers and version words removed, for spotting likely copies. */
export function normalizeForDuplicateMatch(file: IFileItem): string {
  let base: string = stripCopyCounter(fileBaseName(file).toLowerCase());
  base = base.replace(/^copy of\s+/, '');
  base = base.replace(/[_\-.]+/g, ' ');
  base = base.replace(/\b(v\d+|final|new|latest|updated|rev\d*)\b/g, ' ');
  base = base.replace(/\s+/g, ' ').trim();
  return `${base}.${file.extension}`;
}

/**
 * Possible duplicates: same normalized name and extension but not byte-identical
 * (e.g. "Brochure final.md" vs "Brochure final (1).md" with edited content).
 */
export function findPossibleDuplicates(scan: IScanResult): IIssue[] {
  const files: IFileItem[] = candidateFiles(scan);
  const groups: IFileItem[][] = values(groupBy(files, normalizeForDuplicateMatch)).filter(g => g.length > 1);

  const issues: IIssue[] = [];
  for (const group of groups) {
    const hashes: { [hash: string]: boolean } = {};
    let unknownHash: boolean = false;
    for (const f of group) {
      if (f.quickXorHash) {
        hashes[`${f.quickXorHash}|${f.size}`] = true;
      } else {
        unknownHash = true;
      }
    }
    // Every copy identical: already an exact-duplicate issue.
    if (!unknownHash && Object.keys(hashes).length === 1) {
      continue;
    }
    const sorted: IFileItem[] = group.slice().sort((a, b) => filePath(a).localeCompare(filePath(b)));
    issues.push({
      id: `possibleDuplicates:${sorted[0].id}`,
      category: 'possibleDuplicates',
      severity: 'low',
      message: `${sorted.length} files with similar names but different content: "${sorted.map(f => f.name).join('", "')}"`,
      fileIds: sorted.map(f => f.id),
      folderIds: []
    });
  }
  return issues;
}
